import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  PenLine, Mic, Radio, Heart, Ear, GraduationCap,
  Drama, Globe, Sparkles, Headphones, Clock, MessageCircle,
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import LegalFooter from '../components/LegalFooter';
import AperitivoSection from '../components/AperitivoSection';
import { DISCOVER_IMAGES as IMG } from '../config/landingImages';
import { useLandingMetadata } from '../hooks/useLandingMetadata';

/**
 * Pagina pubblica "Cosa puoi fare su Nymia" (/cosa-puoi-fare).
 * F9.5: copy migrato su i18n (namespace `whatYouCanDo`, IT/EN).
 */
const GOLD = '#D4AF37';
const BLACK = '#050505';
const serif = { fontFamily: "'Playfair Display', serif" };
const inter = { fontFamily: "'Inter', 'Manrope', sans-serif" };

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.85, delay: i * 0.08, ease: [0.22, 0.61, 0.36, 1] },
  }),
};

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// ─── Data (structural only — labels live in i18n `whatYouCanDo`) ─────────────
const LIKES = [
  { icon: Mic, target: 'voce' },
  { icon: Headphones, target: 'voce' },
  { icon: PenLine, target: 'scrivi' },
  { icon: Radio, target: 'live-audio' },
  { icon: Heart, target: 'one-to-one' },
  { icon: Ear, target: 'one-to-one' },
  { icon: GraduationCap, target: 'guadagna' },
  { icon: Drama, target: 'live-audio' },
  { icon: Sparkles, target: 'voce' },
  { icon: Globe, target: 'community' },
];

const SECTIONS = [
  { id: 'voce', img: IMG.voice, icon: Mic, badges: ['free', 'paid'] },
  { id: 'scrivi', img: IMG.writing, icon: PenLine, badges: ['free', 'paid'] },
  { id: 'live-audio', img: IMG.liveAudio, icon: Headphones, badges: ['free', 'paid'] },
  { id: 'one-to-one', img: IMG.oneToOne, icon: MessageCircle, badges: ['paid'] },
  { id: 'guadagna', img: IMG.minute, icon: Clock, badges: ['paid'] },
  { id: 'community', img: IMG.community, icon: Globe, badges: ['free'] },
];

// ─── Small pieces ────────────────────────────────────────────────────────────
const GrainOverlay = () => (
  <div
    aria-hidden
    className="pointer-events-none fixed inset-0 z-[60] opacity-[0.05] mix-blend-overlay"
    style={{
      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.9 0 0 0 0 0.9 0 0 0 0 0.85 0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")`,
    }}
  />
);

const Badge = ({ type, t }) => (
  <span
    className={`inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${
      type === 'free'
        ? 'border-white/20 text-white/80'
        : 'border-[#D4AF37]/50 text-[#D4AF37]'
    }`}
    style={inter}
    data-testid={`badge-${type}`}
  >
    {type === 'free' ? t('badges.free') : t('badges.paid')}
  </span>
);

const CtaButtons = ({ primaryLabel, t, testPrefix = 'discover' }) => (
  <div className="flex flex-wrap items-center justify-center gap-3">
    <Link
      to="/register"
      className="rounded-full px-8 py-3 text-sm font-bold text-black transition-opacity hover:opacity-90"
      style={{ ...inter, backgroundColor: GOLD }}
      data-testid={`${testPrefix}-cta-register`}
    >
      {primaryLabel || t('cta.register')}
    </Link>
    <Link
      to="/login"
      className="rounded-full border border-white/20 px-8 py-3 text-sm font-semibold text-white transition-colors hover:border-white/50"
      style={inter}
      data-testid={`${testPrefix}-cta-login`}
    >
      {t('cta.login')}
    </Link>
  </div>
);

// ─── Header ──────────────────────────────────────────────────────────────────
const DiscoverHeader = ({ t }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? 'border-b border-white/5 bg-[#050505]/85 backdrop-blur-xl' : 'bg-[#050505]/60 backdrop-blur-md'
      }`}
      data-testid="discover-header"
    >
      <div className="flex items-center justify-between px-4 py-3.5 md:px-10 md:py-4">
        <Link
          to="/"
          className="transition-opacity hover:opacity-80"
          style={{ ...serif, fontSize: '1.35rem', fontWeight: 500, letterSpacing: '0.08em', color: '#F5F1E8', textDecoration: 'none' }}
          data-testid="discover-header-logo"
        >
          Nymia
        </Link>
        <nav className="flex items-center gap-3 md:gap-8" data-testid="discover-header-nav">
          <Link to="/" className="text-[11px] font-medium text-white/60 transition-colors hover:text-white md:text-[13px]" style={inter} data-testid="discover-nav-home">{t('header.home')}</Link>
          <Link to="/creator" className="hidden text-[11px] font-medium text-white/60 transition-colors hover:text-white sm:block md:text-[13px]" style={inter} data-testid="discover-nav-creator">{t('header.creator')}</Link>
          <span className="text-[11px] font-semibold md:text-[13px]" style={{ ...inter, color: GOLD }} data-testid="discover-nav-cosa">{t('header.cosa')}</span>
          <Link to="/contact" className="hidden text-[11px] font-medium text-white/60 transition-colors hover:text-white sm:block md:text-[13px]" style={inter} data-testid="discover-nav-contatti">{t('header.contatti')}</Link>
        </nav>
        <div className="flex items-center gap-2 md:gap-3">
          <div className="hidden md:block" style={{ opacity: 0.7 }}>
            <LanguageSwitcher variant="compact" testId="discover-lang-switcher" />
          </div>
          <Link
            to="/login"
            className="hidden rounded-full border border-white/15 px-4 py-1.5 text-[12px] font-semibold text-white transition-colors hover:border-white/40 sm:block"
            style={inter}
            data-testid="discover-header-login"
          >
            {t('header.login')}
          </Link>
          <Link
            to="/register"
            className="rounded-full px-4 py-1.5 text-[12px] font-bold text-black transition-opacity hover:opacity-90"
            style={{ ...inter, backgroundColor: GOLD }}
            data-testid="discover-header-register"
          >
            {t('header.register')}
          </Link>
        </div>
      </div>
    </header>
  );
};

// ─── Feature section ─────────────────────────────────────────────────────────
const FeatureSection = ({ section, index, t }) => {
  const reverse = index % 2 === 1;
  const Icon = section.icon;
  const title = t(`sections.${section.id}.title`);
  const lines = t(`sections.${section.id}.lines`, { returnObjects: true });
  const tags = t(`sections.${section.id}.tags`, { returnObjects: true, defaultValue: null });
  const outro = t(`sections.${section.id}.outro`, { defaultValue: '' });
  return (
    <section id={section.id} className="scroll-mt-24 px-5 py-14 md:py-24" data-testid={`discover-section-${section.id}`}>
      <div className={`mx-auto flex max-w-6xl flex-col items-center gap-8 md:gap-16 ${reverse ? 'md:flex-row-reverse' : 'md:flex-row'}`}>
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-15%' }}
          className="w-full md:w-1/2"
        >
          <div className="overflow-hidden rounded-2xl border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
            <img
              src={section.img}
              alt={title}
              loading="lazy"
              className="aspect-[3/2] w-full object-cover transition-transform duration-[2500ms] hover:scale-[1.04]"
            />
          </div>
        </motion.div>
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-15%' }} custom={1}
          className="w-full md:w-1/2"
        >
          <div className="mb-4 flex items-center gap-2">
            <Icon className="h-5 w-5" style={{ color: GOLD }} />
            <div className="flex flex-wrap gap-2">
              {section.badges.map((b) => <Badge key={b} type={b} t={t} />)}
            </div>
          </div>
          <h2 className="text-3xl leading-tight sm:text-4xl md:text-5xl" style={{ ...serif, fontWeight: 500, color: '#F5F1E8' }}>
            {title}
          </h2>
          <div className="mt-5 space-y-2.5">
            {Array.isArray(lines) && lines.map((l) => (
              <p key={l} className="text-base leading-relaxed text-white/65 md:text-lg" style={inter}>{l}</p>
            ))}
          </div>
          {Array.isArray(tags) && tags.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {tags.map((tg) => (
                <span key={tg} className="rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-[12px] text-white/75" style={inter}>
                  {tg}
                </span>
              ))}
            </div>
          )}
          {outro && (
            <p className="mt-5 text-base italic leading-relaxed md:text-lg" style={{ ...serif, color: GOLD, opacity: 0.9 }}>
              {outro}
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
};

// ─── Page ────────────────────────────────────────────────────────────────────
export default function WhatYouCanDoPage() {
  useLandingMetadata('discover');
  const { t } = useTranslation('whatYouCanDo');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [t]);

  const ideas = t('ideas.items', { returnObjects: true });
  const who = t('who.items', { returnObjects: true });

  return (
    <div
      data-testid="discover-page"
      className="relative min-h-screen w-full overflow-x-hidden"
      style={{ backgroundColor: BLACK, color: '#F5F1E8' }}
    >
      <GrainOverlay />
      <DiscoverHeader t={t} />

      {/* HERO */}
      <section className="relative flex min-h-[92vh] items-center justify-center px-6 pt-24" data-testid="discover-hero">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: `radial-gradient(ellipse 70% 50% at 50% 42%, rgba(212,175,55,0.10), transparent 65%)` }}
        />
        <div className="relative mx-auto max-w-3xl text-center">
          <motion.p variants={fadeUp} initial="hidden" animate="show" className="mb-6 text-[11px] uppercase tracking-[0.4em] text-white/40" style={inter}>
            {t('hero.eyebrow')}
          </motion.p>
          <motion.h1
            variants={fadeUp} initial="hidden" animate="show" custom={1}
            className="text-4xl leading-[1.1] sm:text-5xl lg:text-6xl"
            style={{ ...serif, fontWeight: 500 }}
          >
            {t('hero.title_pre')} <span style={{ color: GOLD }}>Nymia</span>
          </motion.h1>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2} className="mx-auto mt-8 max-w-xl space-y-3">
            <p className="text-base leading-relaxed text-white/70 md:text-lg" style={inter}>
              {t('hero.p1')}
            </p>
            <p className="text-base leading-relaxed text-white/70 md:text-lg" style={inter}>
              {t('hero.p2')}
            </p>
            <p className="text-base italic leading-relaxed md:text-lg" style={{ ...serif, color: GOLD }}>
              {t('hero.p3')}
            </p>
          </motion.div>
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="mt-10">
            <CtaButtons t={t} testPrefix="discover-hero" />
          </motion.div>
        </div>
      </section>

      {/* SEZIONE INTERATTIVA */}
      <section className="px-5 py-16 md:py-24" data-testid="discover-likes">
        <div className="mx-auto max-w-5xl">
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-15%' }}
            className="text-center text-3xl sm:text-4xl md:text-5xl"
            style={{ ...serif, fontWeight: 500 }}
          >
            {t('likes.title')}
          </motion.h2>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 md:gap-4">
            {LIKES.map((l, i) => {
              const Icon = l.icon;
              const label = t(`likes.items.${i}`);
              return (
                <motion.button
                  key={`${l.target}-${i}`}
                  variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-8%' }} custom={i % 5}
                  onClick={() => scrollToId(l.target)}
                  className="group flex flex-col items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-6 text-center transition-colors duration-300 hover:border-[#D4AF37]/50 hover:bg-white/[0.06] md:py-8"
                  data-testid={`discover-like-${l.target}-${i}`}
                >
                  <Icon className="h-6 w-6 text-white/50 transition-colors duration-300 group-hover:text-[#D4AF37] md:h-7 md:w-7" strokeWidth={1.5} />
                  <span className="text-[12px] font-medium leading-snug text-white/75 md:text-[13px]" style={inter}>
                    {label}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* SEZIONI PRINCIPALI */}
      {SECTIONS.map((s, i) => <FeatureSection key={s.id} section={s} index={i} t={t} />)}

      {/* APERITIVO NYMIA (gestito da Admin) */}
      <AperitivoSection source="cosa-puoi-fare" />

      {/* TI SERVE UN'IDEA? */}
      <section className="px-5 py-16 md:py-28" data-testid="discover-ideas">
        <div className="mx-auto max-w-6xl">
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-15%' }}
            className="text-center text-3xl sm:text-4xl md:text-5xl"
            style={{ ...serif, fontWeight: 500 }}
          >
            {t('ideas.title')}
          </motion.h2>
          <div className="mt-10 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 md:gap-3">
            {Array.isArray(ideas) && ideas.map((idea, i) => (
              <motion.div
                key={idea}
                variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-5%' }} custom={i % 4}
                className="group flex items-start gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-4 transition-colors duration-300 hover:border-[#D4AF37]/40"
                data-testid={`discover-idea-${i}`}
              >
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/30 transition-colors duration-300 group-hover:text-[#D4AF37]" strokeWidth={1.5} />
                <span className="text-[12.5px] leading-snug text-white/75 md:text-[13px]" style={inter}>{idea}.</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NON SERVE ESSERE FAMOSI */}
      <section className="relative px-6 py-20 md:py-32" data-testid="discover-not-famous">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: `radial-gradient(ellipse 60% 45% at 50% 50%, rgba(212,175,55,0.07), transparent 65%)` }}
        />
        <div className="relative mx-auto max-w-2xl text-center">
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-15%' }}
            className="text-3xl sm:text-4xl md:text-5xl"
            style={{ ...serif, fontWeight: 500 }}
          >
            {t('not_famous.title')}
          </motion.h2>
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-15%' }} custom={1}
            className="mt-8 space-y-2.5"
          >
            <p className="text-base leading-relaxed text-white/65 md:text-lg" style={inter}>{t('not_famous.p1')}</p>
            <p className="text-base leading-relaxed text-white/65 md:text-lg" style={inter}>{t('not_famous.p2')}</p>
            <p className="text-base leading-relaxed text-white/65 md:text-lg" style={inter}>{t('not_famous.p3')}</p>
            <p className="pt-3 text-lg leading-relaxed md:text-xl" style={{ ...serif, color: '#F5F1E8' }}>
              {t('not_famous.p4')}
            </p>
            <p className="text-base italic leading-relaxed md:text-lg" style={{ ...serif, color: GOLD }}>
              {t('not_famous.p5')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* CHI PUÒ USARE NYMIA? */}
      <section className="px-5 py-16 md:py-28" data-testid="discover-who">
        <div className="mx-auto max-w-5xl">
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-15%' }}
            className="text-center text-3xl sm:text-4xl md:text-5xl"
            style={{ ...serif, fontWeight: 500 }}
          >
            {t('who.title')}
          </motion.h2>
          <div className="mt-10 grid grid-cols-2 gap-2.5 sm:grid-cols-4 md:gap-3">
            {Array.isArray(who) && who.map((w, i) => (
              <motion.div
                key={w}
                variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-5%' }} custom={i % 4}
                className={`flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] px-3 py-5 text-center transition-colors duration-300 hover:border-[#D4AF37]/40 ${
                  i === who.length - 1 ? 'col-span-2' : ''
                }`}
                data-testid={`discover-who-${i}`}
              >
                <span className="text-[13px] leading-snug text-white/80 md:text-sm" style={inter}>{w}</span>
              </motion.div>
            ))}
          </div>
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-10%' }}
            className="mx-auto mt-12 max-w-2xl text-center text-lg italic leading-relaxed md:text-xl"
            style={{ ...serif, color: GOLD }}
          >
            {t('who.outro')}
          </motion.p>
        </div>
      </section>

      {/* FINALE */}
      <section className="relative px-6 py-24 md:py-40" data-testid="discover-final">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: `radial-gradient(ellipse 70% 55% at 50% 60%, rgba(212,175,55,0.10), transparent 68%)` }}
        />
        <div className="relative mx-auto max-w-2xl text-center">
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-15%' }}
            className="text-4xl leading-tight sm:text-5xl lg:text-6xl"
            style={{ ...serif, fontWeight: 500 }}
          >
            {t('final.title_pre')} <span style={{ color: GOLD }}>{t('final.title_gold')}</span>.
          </motion.h2>
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-15%' }} custom={1}
            className="mt-8 space-y-3"
          >
            <p className="text-base leading-relaxed text-white/65 md:text-lg" style={inter}>
              {t('final.p1')}
            </p>
            <p className="text-base leading-relaxed text-white/65 md:text-lg" style={inter}>
              {t('final.p2')}
            </p>
            <p className="text-lg leading-relaxed md:text-xl" style={{ ...serif, color: '#F5F1E8' }}>
              {t('final.p3')}
            </p>
            <p className="text-base leading-relaxed text-white/65 md:text-lg" style={inter}>
              {t('final.p4')}
            </p>
          </motion.div>
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-10%' }} custom={2}
            className="mt-12"
          >
            <CtaButtons primaryLabel={t('cta.register_free')} t={t} testPrefix="discover-final" />
          </motion.div>
        </div>
      </section>

      <div className="border-t border-white/5 px-6 py-10">
        <LegalFooter />
      </div>
    </div>
  );
}
